import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, StaggerGroup, StaggerChild } from "@/components/site/Reveal";

/* ------------------------------------------------------------------
   PRICING TIERS — 4-tier Gleap style
------------------------------------------------------------------- */
const TIERS = [
    {
        name: "Starter",
        price: "$49",
        cadence: "/mo",
        blurb: "For solo founders and side projects.",
        features: ["1 seat", "1 bot", "Web chat", "Basic FAQs", "Feature roadmap", "Community support"],
        cta: "Start free",
        highlight: false,
    },
    {
        name: "Team",
        price: "$149",
        cadence: "/mo",
        blurb: "For startups and growing teams.",
        features: [
            "Everything in Starter +",
            "Unlimited seats",
            "Unlimited bots",
            "All support channels",
            "Custom domains",
            "Third-party integrations",
            "Outbound messaging",
        ],
        cta: "Start free",
        highlight: false,
    },
    {
        name: "Pro",
        price: "$299",
        cadence: "/mo",
        blurb: "For teams that want Nyra to investigate, resolve & ship fixes.",
        features: [
            "Everything in Team +",
            "25% off AI tokens",
            "All AI models",
            "Custom agents",
            "Nyra PM",
            "Nyra Voice",
            "Nyra Resolve",
        ],
        cta: "Talk to sales",
        highlight: true,
    },
    {
        name: "Enterprise",
        price: "from $999",
        cadence: "/mo",
        blurb: "For scale and complex compliance.",
        features: [
            "Everything in Pro +",
            "SOC 2 Type II",
            "SSO",
            "BYOK",
            "Pay by invoice",
            "Custom MAU limit",
            "Dedicated CSM",
            "Priority support",
            "Custom SLA",
        ],
        cta: "Contact sales",
        highlight: false,
    },
];

export const PricingTiers = ({ openDemo }) => (
    <section className="py-24 md:py-28 bg-warm" data-testid="pricing-tiers">
        <div className="container-x">
            <Reveal>
                <h2 className="heading-section text-3xl md:text-4xl mb-16 max-w-3xl">
                    Choose your agent stack. Usage-based AI.
                </h2>
            </Reveal>

            <StaggerGroup className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {TIERS.map((t) => (
                    <StaggerChild key={t.name}>
                        <div
                            className={`gleap-card p-6 h-full flex flex-col ${
                                t.highlight ? "border-t-2 border-t-emerald-500" : ""
                            }`}
                            data-testid={`pricing-${t.name.toLowerCase()}`}
                        >
                            <div className="text-slate-900 font-medium">{t.name}</div>
                            <div className="mt-3 flex items-baseline gap-1">
                                <span className="heading-stat text-4xl text-slate-900">
                                    {t.price}
                                </span>
                                <span className="text-slate-500 text-sm">{t.cadence}</span>
                            </div>
                            <p className="text-slate-500 text-sm mt-3 min-h-[48px]">{t.blurb}</p>
                            <div className="mt-6 space-y-2.5 flex-1">
                                {t.features.map((f, i) => (
                                    <div key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                        <i className="fa-solid fa-check text-emerald-600 text-xs mt-1 shrink-0" />
                                        <span>{f}</span>
                                    </div>
                                ))}
                            </div>
                            <button
                                onClick={openDemo}
                                className={t.highlight ? "pill-dark mt-8" : "pill-ghost mt-8"}
                                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                            >
                                {t.cta}
                            </button>
                        </div>
                    </StaggerChild>
                ))}
            </StaggerGroup>
        </div>
    </section>
);

/* ------------------------------------------------------------------
   FEATURE LOOPS — vertical tabs + stacked gradient cards
------------------------------------------------------------------- */
const LOOPS = [
    {
        key: "automate",
        label: "Automate",
        cards: [
            {
                title: "Automate every routine conversation.",
                body: "NyraAI handles high-volume customer interactions across web, WhatsApp, voice, and email — deflecting tier-1 support, qualifying leads, booking appointments, and confirming orders 24×7 without a human hand-off.",
                mesh: "",
                agent: "Nyra · Automate",
                lines: ["Detecting intent…", "Matched 3 knowledge sources", "Reply drafted ✓"],
            },
        ],
    },
    {
        key: "execute",
        label: "Execute",
        cards: [
            {
                title: "Execute the workflow, not just the reply.",
                body: "Nyra reads policies, calls your CRM/ERP/telephony, processes payments, updates records, and routes complex cases to a human — all in one continuous loop, with a full audit trail on every action.",
                mesh: "mesh-b",
                agent: "Nyra · Execute",
                lines: ["Validating request…", "Calling Salesforce API", "Workflow completed ✓"],
            },
        ],
    },
    {
        key: "scale",
        label: "Scale",
        cards: [
            {
                title: "Scale globally in every language.",
                body: "One conversation logic runs across 22+ Indian and international languages, every channel, and any volume — from 1,000 to 10 million conversations a month — with consistent brand voice and 85%+ auto-resolution.",
                mesh: "mesh-c",
                agent: "Nyra · Scale",
                lines: ["Auto-switch: EN → हिन्दी", "Concurrent sessions: 12,483", "Auto-resolved ✓"],
            },
        ],
    },
];

export const FeatureLoops = () => {
    const [active, setActive] = useState(0);
    const activeLoop = LOOPS[active];
    return (
        <section className="py-24 md:py-28 bg-warm" data-testid="feature-loops">
            <div className="container-x">
                <div className="grid md:grid-cols-12 gap-10">
                    {/* Vertical tabs */}
                    <div className="md:col-span-3">
                        <div className="sticky top-24">
                            {LOOPS.map((l, i) => (
                                <button
                                    key={l.key}
                                    onClick={() => setActive(i)}
                                    className={`vtab block w-full text-left ${active === i ? "active" : ""}`}
                                    data-testid={`loop-tab-${l.key}`}
                                >
                                    {l.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Cards */}
                    <div className="md:col-span-9 space-y-6">
                        <AnimatePresence mode="wait">
                            {activeLoop.cards.map((c, i) => (
                                <motion.div
                                    key={activeLoop.key + i}
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                    className="gleap-card p-6 md:p-8 grid md:grid-cols-2 gap-6"
                                    data-testid={`loop-card-${activeLoop.key}`}
                                >
                                    <div>
                                        <div className="text-slate-900 font-medium mb-3">{c.title}</div>
                                        <p className="text-slate-500 text-sm leading-relaxed">{c.body}</p>
                                    </div>
                                    <div className={`mesh-frame ${c.mesh} min-h-[240px] flex items-center justify-center`}>
                                        <div className="glass-card p-6 w-full max-w-xs">
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className="w-6 h-6 rounded-full bg-slate-900 grid place-items-center">
                                                    <i className="fa-solid fa-robot text-white text-[10px]" />
                                                </div>
                                                <div className="text-xs font-medium text-slate-800">
                                                    {c.agent}
                                                </div>
                                            </div>
                                            <div className="space-y-2 text-[11px]">
                                                {c.lines.map((line, idx) => {
                                                    const isFinal = idx === c.lines.length - 1;
                                                    return (
                                                        <div
                                                            key={idx}
                                                            className={
                                                                isFinal
                                                                    ? "bg-slate-900 text-white rounded px-2 py-1.5"
                                                                    : "bg-white/80 rounded px-2 py-1.5 text-slate-700"
                                                            }
                                                        >
                                                            {line}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
};
